<template>
  <div v-if="feedbackEntries.length > 0" class="mt-2">
    <!-- Toggle button -->
    <button
      @click="showHistory = !showHistory"
      class="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
    >
      <ChevronDownIcon v-if="showHistory" class="h-3 w-3" />
      <ChevronRightIcon v-else class="h-3 w-3" />
      <ClockIcon class="h-3 w-3" />
      <span>{{ showHistory ? 'Verberg' : 'Toon' }} eerdere feedback</span>
    </button>

    <!-- History list -->
    <div v-if="showHistory" class="mt-2 space-y-2">
      <div
        v-for="entry in feedbackEntries"
        :key="entry.id"
        class="pl-4 border-l-2 border-gray-200 dark:border-gray-600 py-1"
      >
        <div class="flex items-center gap-2">
          <span
            class="text-xs px-1.5 py-0.5 rounded font-medium"
            :class="statusBadgeClasses(entry.new_status)"
          >
            {{ statusLabel(entry.new_status) }}
          </span>
        </div>
        <p class="text-xs text-gray-700 dark:text-gray-300 mt-0.5">
          {{ cleanComment(entry.comment) }}
        </p>
        <p class="text-xs text-gray-400 dark:text-gray-500">
          Door {{ entry.changed_by_name }} op {{ formatDateTime(entry.created_at) }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { ChevronRightIcon, ChevronDownIcon, ClockIcon } from '@heroicons/vue/24/outline'
import { workflowService } from '@/services/workflowService.js'
import { getStandardStatusLabel, getWorkflowStatusBadgeClass } from '@/utils/statusBadge'

const props = defineProps({
  prospectId: {
    type: Number,
    required: true
  }
})

const showHistory = ref(false)
const historyItems = ref([])
const isLoading = ref(false)

const AUTOMATED_COMMENTS = new Set([
  'Aangeboden voor beoordeling',
  'Goedgekeurd',
  'Te bespreken',
  'Opnieuw aangeboden na aanpassing'
])

const STRIP_PREFIXES = ['Opnieuw beoordelen: ', 'Afgekeurd door sourcing: ', 'Opnieuw aanvragen: ']

const feedbackEntries = computed(() => {
  const meaningful = historyItems.value.filter(entry => {
    if (!entry.comment) return false
    return !AUTOMATED_COMMENTS.has(entry.comment)
  })
  // Skip de eerste (meest recente) — die toont het parent-component al
  return meaningful.slice(1)
})

function cleanComment(comment) {
  if (!comment) return ''
  for (const prefix of STRIP_PREFIXES) {
    if (comment.startsWith(prefix)) {
      return comment.slice(prefix.length)
    }
  }
  return comment
}

const statusLabel = getStandardStatusLabel

const statusBadgeClasses = getWorkflowStatusBadgeClass

function formatDateTime(dateString) {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

async function fetchHistory() {
  isLoading.value = true
  try {
    historyItems.value = await workflowService.getHistory(props.prospectId)
  } catch {
    historyItems.value = []
  } finally {
    isLoading.value = false
  }
}

watch(
  () => props.prospectId,
  () => {
    showHistory.value = false
    historyItems.value = []
    fetchHistory()
  }
)

onMounted(() => {
  fetchHistory()
})
</script>
